import { z } from "zod";
import {
  CreateTaskDto,
  RecurrenceFrequencySchema,
  ScheduleTaskDto,
  Task,
  TaskStatus,
  TaskStatusSchema,
  TaskType,
  TaskTypeSchema,
  UpdateTaskDto,
} from "@myapp/contract";
import {
  formatDateForInput,
  fromDatetimeLocal,
  toDatetimeLocal,
} from "../../../shared/helpers/date";
import {
  durationsFromInstants,
  firstInstantOutOfOrder,
  instantsFromSlot,
  overrideFields,
  type ActualInstants,
  type ScheduleOverrides,
  type ScheduleSlot,
} from "../../../shared/lib/schedule";

/**
 * One form for create and edit, and for both task types — which now means *one* form, not two
 * behind a switch.
 *
 * THE RULE THIS FILE ENCODES:
 *   Der Handwerker ist Pflicht. Sonst nichts.
 *   Termin und Abgabefrist sind beide optional und unabhängig voneinander: eine Aufgabe kann
 *   beides haben, eines von beidem, oder keines — dann bleibt sie ungeplant und wird später im
 *   Kalender geplant.
 *   Der Aufgabentyp entscheidet über **gar nichts** mehr. Er ist nur noch ein Etikett für Filter,
 *   Liste und Arbeitszeit-Report.
 *
 * WHY THE TYPE DROPPED OUT: it never had a meaning outside this file. `CreateTaskDtoSchema`
 * accepts `plannedStart` and `deadline` in any combination, `CreateTaskUseCase` never reads
 * `taskType` at all, and the API's own `deadlineForOccurrence` carries a deadline across a whole
 * series — i.e. the backend has always assumed one task can hold both. The list page has drawn a
 * Datum *and* an Abgabefrist column per row for just as long. The split lived here alone.
 *
 * WHAT STILL BINDS THE TERMIN TOGETHER: `CreateTaskUseCase` only reserves a slot when a technician
 * AND a start are both present — otherwise it saves an unscheduled task and **silently drops the
 * durations**. So a start without a work duration is worse than no start at all, and that pairing
 * is the one thing `superRefine` below still checks.
 *
 * THE OTHER TRAP: `plannedStart` is the **work** start and round-trips through
 * `schedule.plannedWorkStartsAt`, never `schedule.startsAt` — the latter is the drawn block and
 * begins `plannedTravelMinutes` earlier. Reading it back would walk every task backwards on save.
 *
 * THE THIRD TRAP, and what `toSlot` / `applySlot` exist for: occupancy is `COALESCE(actual, planned)`
 * per component, so a **completed** Aufgabe is drawn from its Ist-Zeiten. The values the board reads
 * and writes therefore have to be the Ist ones for as long as the form says the task is done —
 * reading `plannedStart` there would make the block jump off the position it was drawn at the moment
 * the form opened, and writing it back would save a move that never becomes visible. And in reverse:
 * actuals sent for a task that is *not* completed would resize its block with a 200 and no error
 * anywhere. `taskHasActuals` decides the side in exactly one place, here.
 *
 * IST-ZEITEN ARE INSTANTS, NOT DURATIONS (Task 2040). The office edits the same four moments the
 * Handwerker's taps stamp — Anfahrt ab, Arbeit ab, Arbeit bis, Abfahrt bis — and the server derives
 * the Ist-Minuten from them. Typing a duration used to leave the end instant untouched, and an
 * Aufgabe the office had set to „Abgeschlossen" went on reading as „Läuft" everywhere the phase is
 * derived from the instants. With instants there is nothing left to drift.
 */

/**
 * Actuals are collected once the Handwerker is done, so the form offers them for the one completed
 * state. The task twin of `hasActuals` in `features/appointment-upsert` — single source of truth for
 * both the fields and the update mapper, which must not drift apart.
 */
export function taskHasActuals(status?: TaskStatus): boolean {
  return status === "COMPLETED";
}

const recurrenceFormSchema = z.object({
  frequency: RecurrenceFrequencySchema,
  until: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Format TT.MM.JJJJ"),
});

/**
 * German messages on every constraint, including the ones that are hard to reach.
 *
 * Zod's defaults are English ("Number must be greater than or equal to 0"), and this UI is not.
 * The duration fields rarely surface one — `FormInput` maps an empty box to `""` and `z.coerce`
 * turns that into `0` — but a pasted `-30` or `1.5` reaches them, and an untranslated sentence in
 * the middle of a German form is worse than the field it belongs to.
 */
const MINUTES = { invalid: "Ganze Minuten angeben", negative: "Darf nicht negativ sein" } as const;

/** What a mis-ordered instant is told, keyed by the field that came too early. */
export const INSTANT_ORDER_MESSAGES: Record<keyof ActualInstants, string> = {
  travelStartedAt: "Ungültiger Zeitpunkt",
  actualStart: "Muss nach „Anfahrt ab“ liegen",
  actualEnd: "Muss nach „Arbeit ab“ liegen",
  departureEndedAt: "Muss nach „Arbeit bis“ liegen",
};

export const taskFormSchema = z
  .object({
    title: z.string().min(1, "Pflichtfeld"),
    description: z.string(),
    taskType: TaskTypeSchema,
    /** Edit only — a new task is always PLANNED. */
    status: TaskStatusSchema,
    /** Stays nullable so the empty form has a valid default; emptiness is caught below. */
    assignedTechnicianId: z.number().int().positive("Pflichtfeld").nullable(),
    /** `<input type="datetime-local">` value — local wall clock, no timezone. */
    plannedStart: z.string(),
    /** `<input type="date">` value. Optional, and open to both types. */
    deadline: z.string(),
    plannedWorkDuration: z.coerce.number().int(MINUTES.invalid).min(0, MINUTES.negative),
    plannedTravelDuration: z.coerce.number().int(MINUTES.invalid).min(0, MINUTES.negative),
    plannedDepartureDuration: z.coerce.number().int(MINUTES.invalid).min(0, MINUTES.negative),
    /**
     * Ist-Zeitpunkte, all `<input type="datetime-local">` values. Always present in the form state
     * so the inputs stay controlled, but only surfaced and only submitted for a completed task —
     * see `taskHasActuals`. `""` is "not recorded" and goes out as `null`.
     *
     * `actualStart` is the Ist **work** start, the twin of `plannedStart` and subject to the same
     * trap: it round-trips through `schedule.actualWorkStartsAt`, never through `startsAt`.
     */
    travelStartedAt: z.string(),
    actualStart: z.string(),
    actualEnd: z.string(),
    departureEndedAt: z.string(),
    attachments: z.array(z.string()),
    /** `null` === einmalig. Only offered on create; the frequency is not patchable. */
    recurrence: recurrenceFormSchema.nullable(),
    /** Edit only, and only meaningful on a task that has a `seriesId`. */
    forFullSeries: z.boolean(),
  })
  .superRefine((data, ctx) => {
    const issue = (path: string, message: string) =>
      ctx.addIssue({ code: z.ZodIssueCode.custom, message, path: [path] });

    // Der einzige Pflichtwert. Eine Aufgabe ohne Termin ist gültig, eine ohne Handwerker nicht.
    if (data.assignedTechnicianId == null) issue("assignedTechnicianId", "Pflichtfeld");

    // Kein Termin: nichts weiter zu prüfen. Die Aufgabe wird ungeplant gespeichert und kann
    // später im Kalender geplant werden. Die Abgabefrist ist ebenfalls freiwillig.
    if (!data.plannedStart) {
      // Ausnahme: eine Serie ist eine Menge von Reservierungen und braucht daher einen Anker.
      // `RecurrenceField` sperrt sich ohne `startDate` zwar selbst, aber eine Regel, die *vor*
      // dem Leeren des Starts gesetzt wurde, überlebt das — und der Contract verlangt, dass eine
      // `seriesId` immer einen Termin hat.
      if (data.recurrence) issue("plannedStart", "Für eine Serie ist ein Termin nötig");
      return;
    }

    if (!fromDatetimeLocal(data.plannedStart)) {
      issue("plannedStart", "Ungültiges Datum");
    }

    // Half a termin is worse than none: the API would accept it, save the task unscheduled and
    // throw the times away without an error. Only reachable once a start is actually set.
    if (data.plannedWorkDuration <= 0) issue("plannedWorkDuration", "Pflichtfeld");

    // Ist-Zeiten: an Abschluss needs a start and an end, and the four must read as one session —
    // the server refuses anything else with 400, and a field error beats a toast.
    if (taskHasActuals(data.status)) {
      if (!fromDatetimeLocal(data.actualStart)) issue("actualStart", "Pflichtfeld");
      if (!fromDatetimeLocal(data.actualEnd)) issue("actualEnd", "Pflichtfeld");
      const outOfOrder = firstInstantOutOfOrder(data);
      if (outOfOrder) issue(outOfOrder, INSTANT_ORDER_MESSAGES[outOfOrder]);
    }
  });

export type TaskFormValues = z.infer<typeof taskFormSchema>;

export function getDefaultValues(taskType: TaskType = "GENERAL_TASK"): TaskFormValues {
  return {
    title: "",
    description: "",
    taskType,
    status: "PLANNED",
    assignedTechnicianId: null,
    plannedStart: "",
    deadline: "",
    plannedWorkDuration: 60,
    plannedTravelDuration: 0,
    plannedDepartureDuration: 0,
    travelStartedAt: "",
    actualStart: "",
    actualEnd: "",
    departureEndedAt: "",
    attachments: [],
    recurrence: null,
    forFullSeries: false,
  };
}

/** `YYYY-MM-DD` from a date input → ISO, anchored at local midnight so it survives the round trip. */
function fromDateInput(value: string): string | null {
  const m = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const [, y, mo, d] = m;
  return new Date(Number(y), Number(mo) - 1, Number(d)).toISOString();
}

/**
 * True when the form describes a real reservation — the single question every mapper below asks,
 * and the only thing that decides whether a task is scheduled. Both halves are required because
 * the backend only creates a `ScheduleEntry` when it has a technician *and* a start.
 *
 * There is deliberately no `ownsTermin` beside it any more: the task type used to veto a termin
 * before this function ever ran, which is exactly the rule that went away.
 */
export function isScheduled(values: TaskFormValues): boolean {
  return (
    values.assignedTechnicianId != null && fromDatetimeLocal(values.plannedStart) != null
  );
}

/** Everything `toSlot` and `applySlot` read. `status` decides which side of the COALESCE they use. */
export type SlotValues = Pick<
  TaskFormValues,
  | "assignedTechnicianId"
  | "plannedStart"
  | "plannedTravelDuration"
  | "plannedWorkDuration"
  | "plannedDepartureDuration"
  | "travelStartedAt"
  | "actualStart"
  | "actualEnd"
  | "departureEndedAt"
> & { status?: TaskStatus };

/** The four Ist fields of a form, as the shared helpers read them. */
export function actualInstantsOf(values: SlotValues): ActualInstants {
  return {
    travelStartedAt: values.travelStartedAt,
    actualStart: values.actualStart,
    actualEnd: values.actualEnd,
    departureEndedAt: values.departureEndedAt,
  };
}

/**
 * The termin as a `ScheduleSlot` — **what the calendar draws** while the form is open, which for a
 * completed Aufgabe is its Ist-Zeiten and otherwise its Plan-Zeiten.
 *
 * The returned keys are named after the planned fields because they are the RHF field names the
 * board never inspects; see the note on `ScheduleSlot` itself. What they carry is the side
 * `taskHasActuals` selects — on the Ist side, the durations are *derived* from the four instants
 * exactly as the server will derive them, with the planned Anfahrt/Abfahrt standing in for an
 * instant that was not recorded (the server's occupancy COALESCE does the same).
 *
 * `null` means "there is no block to draw", which for a task is an ordinary state and not an error:
 * any task may sit unplanned, without a Handwerker or without a start. It is also `null` for a
 * duration input the user has just emptied (`valueAsNumber` hands back `NaN`), or an Ist start or
 * end still being typed — the board then keeps the last complete position rather than drawing the
 * block at 06:00.
 *
 * The task type is not consulted and is not even in the `Pick`: every type can be drawn.
 *
 * The start stays the datetime-local **work** start, exactly as the board's `slotToDraft` expects
 * it. Mirrors `features/appointment-upsert`.
 */
export function toSlot(values: SlotValues): ScheduleSlot | null {
  if (values.assignedTechnicianId == null) return null;

  if (taskHasActuals(values.status)) {
    if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(values.actualStart)) return null;
    const durations = durationsFromInstants(actualInstantsOf(values), {
      travel: values.plannedTravelDuration,
      departure: values.plannedDepartureDuration,
    });
    if (!durations) return null;
    return {
      assignedTechnicianId: values.assignedTechnicianId,
      plannedStart: values.actualStart,
      plannedTravelDuration: durations.travel,
      plannedWorkDuration: durations.work,
      plannedDepartureDuration: durations.departure,
    };
  }

  const { plannedStart: start } = values;
  const travel = values.plannedTravelDuration;
  const work = values.plannedWorkDuration;
  const departure = values.plannedDepartureDuration;

  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(start)) return null;
  if (![travel, work, departure].every(Number.isFinite)) return null;

  return {
    assignedTechnicianId: values.assignedTechnicianId,
    plannedStart: start,
    plannedTravelDuration: travel,
    plannedWorkDuration: work,
    plannedDepartureDuration: departure,
  };
}

/**
 * The Ist fields a slot translates to — what the board's drag and the Plan→Ist copy write.
 * Datetime-local in, datetime-local out.
 */
export function slotToActualInstants(slot: ScheduleSlot): ActualInstants {
  return instantsFromSlot(slot.plannedStart, {
    travel: slot.plannedTravelDuration,
    work: slot.plannedWorkDuration,
    departure: slot.plannedDepartureDuration,
  });
}

/**
 * The inverse of `toSlot`: a slot chosen elsewhere — the board — written onto whichever side this
 * form's status puts it on.
 *
 * Used by the seeding `reset`, where the picked slot has to win over the loaded task. A plain spread
 * of the slot cannot do it any more: its keys are the *planned* field names, so on a completed
 * Aufgabe it would fill the Plan-Zeiten with a position the block does not use and leave the
 * Ist-Zeiten — the ones actually drawn — untouched. Mirrors `features/appointment-upsert`.
 */
export function applySlot(values: TaskFormValues, slot?: ScheduleSlot): TaskFormValues {
  if (!slot) return values;

  const scheduling = taskHasActuals(values.status)
    ? slotToActualInstants(slot)
    : {
        plannedStart: slot.plannedStart,
        plannedTravelDuration: slot.plannedTravelDuration,
        plannedWorkDuration: slot.plannedWorkDuration,
        plannedDepartureDuration: slot.plannedDepartureDuration,
      };

  return {
    ...values,
    assignedTechnicianId: slot.assignedTechnicianId,
    ...scheduling,
  };
}

/** Open to every task now, and independent of the termin — a task may be bounded by both. */
function deadlineIso(values: TaskFormValues): string | null {
  return values.deadline ? fromDateInput(values.deadline) : null;
}

export function toCreateDto(
  values: TaskFormValues,
  overrides: ScheduleOverrides = {},
): CreateTaskDto {
  const scheduled = isScheduled(values);

  return {
    title: values.title,
    description: values.description || undefined,
    taskType: values.taskType,
    assignedTechnicianId: values.assignedTechnicianId ?? null,
    plannedStart: scheduled ? fromDatetimeLocal(values.plannedStart) : null,
    deadline: deadlineIso(values) ?? undefined,
    // The three are required by the DTO even for a task that is never reserved; zeroes say
    // "no block" rather than leaving a duration the API would ignore anyway.
    plannedWorkDuration: scheduled ? values.plannedWorkDuration : 0,
    plannedTravelDuration: scheduled ? values.plannedTravelDuration : 0,
    plannedDepartureDuration: scheduled ? values.plannedDepartureDuration : 0,
    attachments: values.attachments.length ? values.attachments : undefined,
    // A series is a set of reservations, so it only exists where a termin does — which is now a
    // question about the termin alone, guarded in `superRefine` rather than by the type.
    recurrence: values.recurrence ?? undefined,
    ...overrideFields(overrides),
  };
}

/**
 * `recurrence` is deliberately absent: `UpdateTaskDtoSchema` does not accept it — changing a
 * frequency would mean re-reserving every future occurrence, which goes through
 * `PATCH /tasks/series/:seriesId` instead.
 *
 * The schedule fields are only sent when the task ALREADY owns an entry. `UpdateTaskUseCase`
 * silently ignores a schedule-shaped patch on an unscheduled task, so adding a termin to one
 * goes through `toScheduleDto` and `PATCH /tasks/:id/schedule`.
 *
 * The overrides are set only after the user confirmed the matching 409 dialog; without a schedule
 * patch they are inert, because the API only runs the checks they waive when the timing changes.
 *
 * The four Ist-Zeitpunkte ride along only for a completed task. `actualStart` and `actualEnd` are
 * guaranteed by `superRefine`; the outer two go out as `null` when empty **on purpose**: a hidden
 * „Anfahrt ab" (no Anfahrt planned) must clear a stale instant, and the server then derives an
 * honest 0 for the Anfahrt minutes. Nothing here sends a duration — the server derives them.
 *
 * The planned keys keep being sent unchanged on a completed task, deliberately: they carry what was
 * planned, the drag never touched them, and re-sending the loaded values preserves the comparison.
 */
export function toUpdateDto(
  values: TaskFormValues,
  options: ScheduleOverrides & { hasSeries: boolean; hasSchedule: boolean },
): UpdateTaskDto {
  const scheduled = isScheduled(values);

  return {
    title: values.title,
    description: values.description || null,
    taskType: values.taskType,
    status: values.status,
    // The DTO has no `null` here: unassigning is not a PATCH, it is a delete of the reservation.
    assignedTechnicianId: values.assignedTechnicianId ?? undefined,
    deadline: deadlineIso(values),
    attachments: values.attachments,
    ...(options.hasSchedule && scheduled
      ? {
          plannedStart: fromDatetimeLocal(values.plannedStart) as string,
          plannedWorkDuration: values.plannedWorkDuration,
          plannedTravelDuration: values.plannedTravelDuration,
          plannedDepartureDuration: values.plannedDepartureDuration,
          ...(taskHasActuals(values.status)
            ? {
                travelStartedAt: fromDatetimeLocal(values.travelStartedAt),
                actualStart: fromDatetimeLocal(values.actualStart) as string,
                actualEnd: fromDatetimeLocal(values.actualEnd) as string,
                departureEndedAt: fromDatetimeLocal(values.departureEndedAt),
              }
            : {}),
        }
      : {}),
    ...(options.hasSeries ? { forFullSeries: values.forFullSeries } : {}),
    ...overrideFields(options),
  };
}

/**
 * The payload for `PATCH /tasks/:id/schedule` — create-or-replace of the reservation. Returns
 * null when the form has no complete termin, which is a valid state for any task.
 */
export function toScheduleDto(
  values: TaskFormValues,
  overrides: ScheduleOverrides = {},
): ScheduleTaskDto | null {
  if (!isScheduled(values)) return null;

  return {
    plannedStart: fromDatetimeLocal(values.plannedStart) as string,
    plannedWorkDuration: values.plannedWorkDuration,
    plannedTravelDuration: values.plannedTravelDuration,
    plannedDepartureDuration: values.plannedDepartureDuration,
    ...overrideFields(overrides),
  };
}

export function fromTask(task: Task): TaskFormValues {
  const s = task.schedule;

  /**
   * Ist-Zeiten are nullable until the Handwerker reports them. A completed Aufgabe that was never
   * *started* has no Ist instants at all, and opening the form on four blanks would publish nothing
   * to the calendar — so what was planned stands in, laid out as instants, which is also the sensible
   * starting point for a manager correcting them. Stored instants always win; the stored minutes
   * decide the length of a phase whose instant is missing (a legacy row the office typed minutes on).
   */
  const plannedAsInstants = s
    ? instantsFromSlot(toDatetimeLocal(s.actualWorkStartsAt ?? s.plannedWorkStartsAt), {
        travel: s.actualTravelMinutes ?? s.plannedTravelMinutes,
        work: s.actualWorkMinutes ?? s.plannedWorkMinutes,
        departure: s.actualDepartureMinutes ?? s.plannedDepartureMinutes,
      })
    : { travelStartedAt: "", actualStart: "", actualEnd: "", departureEndedAt: "" };

  return {
    title: task.title,
    description: task.description ?? "",
    taskType: task.taskType,
    status: task.status,
    assignedTechnicianId: task.assignedTechnicianId,
    // plannedWorkStartsAt, NOT startsAt — see the note at the top of this file.
    plannedStart: toDatetimeLocal(s?.plannedWorkStartsAt),
    deadline: formatDateForInput(task.deadline),
    plannedWorkDuration: s?.plannedWorkMinutes ?? 60,
    plannedTravelDuration: s?.plannedTravelMinutes ?? 0,
    plannedDepartureDuration: s?.plannedDepartureMinutes ?? 0,
    travelStartedAt: toDatetimeLocal(s?.travelStartedAt) || plannedAsInstants.travelStartedAt,
    actualStart: toDatetimeLocal(s?.actualWorkStartsAt) || plannedAsInstants.actualStart,
    actualEnd: toDatetimeLocal(s?.actualEndsAt) || plannedAsInstants.actualEnd,
    departureEndedAt:
      toDatetimeLocal(s?.departureEndedAt) || plannedAsInstants.departureEndedAt,
    attachments: task.attachments ?? [],
    recurrence: null,
    forFullSeries: false,
  };
}
